/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2026 (original work) Open Assessment Technologies SA
 */

import { writable, get } from 'svelte/store';
import { parsePositionLabel, parseCurrentTimerLevel } from '@/core/utils/parseInteractionsArray';

// stores interactions array
export const interactions = writable([]);

// stores selected interactions, if any
export const selectedInteractions = writable([]);

function normalizeInteraction(interaction) {
    parsePositionLabel(interaction);
    parseCurrentTimerLevel(interaction);

    return interaction;
}

/**
 * Updates interactions store with received newInteraction.
 * If newInteraction dxId already exists, outdated itneraction is overwritten with received interaction
 * If newInteraction dxId does not exist, we push newInteraction, but first we check if newInteraction
 * and any not started interaction share same tt id, and remove stored interaction in case
 * it is (it means is a NRPS user moving from not started into in progress)
 * @param {Object} newInteraction - received interaction from WS
 */
export function update(newInteraction) {
    normalizeInteraction(newInteraction);

    interactions.update(storeValue => {
        if (newInteraction.deliveryExecution?.id) {
            // get array of dxIds
            const dxIds = storeValue
                .filter(interaction => interaction.deliveryExecution && interaction.deliveryExecution.id)
                .map(interaction => interaction.deliveryExecution.id);

            // If newInteraction dxId already exists, outdated itneraction is overwritten with received interaction
            if (dxIds.includes(newInteraction.deliveryExecution.id)) {
                storeValue = storeValue.map(interaction => {
                    if (interaction.deliveryExecution?.id === newInteraction.deliveryExecution.id) {
                        // preserve TT data
                        newInteraction.user = interaction.user;
                        // if newInteraction is one of the selected interactions (same dxId), we update it
                        const newSelectedInteractions = get(selectedInteractions).map(selectedInteraction => {
                            if (
                                selectedInteraction.deliveryExecution &&
                                selectedInteraction.deliveryExecution.id === newInteraction.deliveryExecution.id
                            ) {
                                return newInteraction;
                            }
                            return selectedInteraction;
                        });
                        selectedInteractions.set(newSelectedInteractions);
                        return newInteraction;
                    } else {
                        return interaction;
                    }
                });
            } else {
                // if newInteraction dxId is new, we check if newInteraction user id already existed (NRPS users or same user taking multiple deliveries)
                const interactionIndex = storeValue.findIndex(
                    interaction => interaction.user.id === newInteraction.user.id
                );

                if (interactionIndex !== -1) {
                    // preserve TT data
                    newInteraction.user = storeValue[interactionIndex].user;
                    // If the existing interaction is a not started row, remove it before pushing the live interaction
                    // so the same user does not appear in both not started and in progress tabs.
                    if (storeValue[interactionIndex].isNotStartedUser || !storeValue[interactionIndex].deliveryExecution) {
                        storeValue.splice(interactionIndex, 1);
                    }
                }
                // push new interaction
                storeValue = [...storeValue, newInteraction];
            }
        }

        return storeValue;
    });
}

/**
 * When internet connection is lost and recovered, we need to update interactions store
 * but also selected interactions, because could be the case that they have updated
 * info. This is not necessary on initial bootstrap (no interactions selected),
 * group change (same dxId can not be on multiple contexts) or
 * returning to Monitoring (WS is handling it)
 * @param {Array} newInteractions - Array of updated interactions
 */
export function recoverInteractionsState(newInteractions) {
    const normalizedInteractions = newInteractions.map(interaction => normalizeInteraction(interaction));
    const deprecatedSelectedInteractions = get(selectedInteractions);
    if (deprecatedSelectedInteractions.length > 0) {
        let newInteraction = null;
        const updatedSelectedInteractions = deprecatedSelectedInteractions.map(deprecatedInteraction => {
            if (
                deprecatedInteraction.deliveryExecution &&
                deprecatedInteraction.deliveryExecution.id &&
                (newInteraction = normalizedInteractions.find(
                    interaction =>
                        interaction.deliveryExecution &&
                        interaction.deliveryExecution.id &&
                        interaction.deliveryExecution.id === deprecatedInteraction.deliveryExecution.id
                ))
            ) {
                return newInteraction;
            }
            return deprecatedInteraction;
        });
        selectedInteractions.set(updatedSelectedInteractions);
    }
    interactions.set(normalizedInteractions);
}
